import React, { useState, useEffect } from 'react';
import authenticationService from '../../services/authenticationService';

const AppPasswordPolicy = () => {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadPolicy();
  }, []);

  const loadPolicy = async () => {
    try {
      setLoading(true);
      const data = await authenticationService.getAppDefaults();
      setPolicy(data.passwordPolicy || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await authenticationService.updateAppDefaultPasswordPolicy(policy);

      setSuccess('App-level password policy updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
  }

  if (!policy) {
    return <div>Failed to load password policy</div>;
  }

  return (
    <div>
      <div style={{
        padding: '16px',
        backgroundColor: '#eff6ff',
        border: '1px solid #3b82f6',
        borderRadius: '8px',
        marginBottom: '24px',
        fontSize: '14px',
        color: '#1e40af'
      }}>
        <strong>App-Level Default Password Policy</strong>
        <p style={{ margin: '8px 0 0 0' }}>
          This policy applies to FreeUser/ProUser accounts and serves as the minimum security floor for all Teams and Organizations. 
          Teams/Organizations can strengthen these requirements but cannot weaken them below these values.
        </p>
      </div>

      {/* Error/Success messages */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#991b1b',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#d1fae5',
          border: '1px solid #6ee7b7',
          borderRadius: '8px',
          color: '#065f46',
          marginBottom: '16px'
        }}>
          {success}
        </div>
      )}

      {/* Policy Form */}
      <div style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '24px'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '20px' }}>
          Password Requirements
        </h3>

        <div style={{ display: 'grid', gap: '20px' }}>
          {/* Minimum Length */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              Minimum Length: {policy.minLength || 8} characters
            </label>
            <input
              type="range"
              min={6}
              max={64}
              value={policy.minLength || 8}
              onChange={(e) => setPolicy({ ...policy, minLength: parseInt(e.target.value) })}
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              <span>6</span>
              <span>64</span>
            </div>
          </div>

          {/* Complexity Requirements */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '12px' }}>
              Complexity Requirements
            </label>
            <div style={{ display: 'grid', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.requireUppercase !== false}
                  onChange={(e) => setPolicy({ ...policy, requireUppercase: e.target.checked })}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>Require uppercase letter</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.requireLowercase !== false}
                  onChange={(e) => setPolicy({ ...policy, requireLowercase: e.target.checked })}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>Require lowercase letter</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.requireDigit !== false}
                  onChange={(e) => setPolicy({ ...policy, requireDigit: e.target.checked })}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>Require digit</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.requireSpecialChar !== false}
                  onChange={(e) => setPolicy({ ...policy, requireSpecialChar: e.target.checked })}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>Require special character</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={policy.allowPassphrase || false}
                  onChange={(e) => setPolicy({ ...policy, allowPassphrase: e.target.checked })}
                  style={{ width: '18px', height: '18px', marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>Allow passphrases (spaces and longer passwords)</span>
              </label>
            </div>
          </div>

          {/* Lockout Threshold */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              Maximum Failed Login Attempts: {policy.maxFailedAttempts || 5}
            </label>
            <input
              type="range"
              min={3}
              max={10}
              value={policy.maxFailedAttempts || 5}
              onChange={(e) => setPolicy({ ...policy, maxFailedAttempts: parseInt(e.target.value) })}
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              <span>3</span>
              <span>10</span>
            </div>
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              Account will be locked after {policy.maxFailedAttempts || 5} failed login attempts
            </p>
          </div>

          {/* Password History */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
              Password History Count
            </label>
            <input
              type="number"
              min={0}
              max={20}
              value={policy.passwordHistoryCount || 0}
              onChange={(e) => setPolicy({ ...policy, passwordHistoryCount: parseInt(e.target.value) || 0 })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              {policy.passwordHistoryCount === 0 
                ? 'Users can reuse previous passwords' 
                : `Users cannot reuse their last ${policy.passwordHistoryCount} passwords`}
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '10px 24px',
              backgroundColor: saving ? '#9ca3af' : '#667eea',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.target.style.backgroundColor = '#5568d3';
              }
            }}
            onMouseLeave={(e) => {
              if (!saving) {
                e.target.style.backgroundColor = '#667eea';
              }
            }}
          >
            {saving ? 'Saving...' : 'Save Policy'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppPasswordPolicy;

